
import { isEqual, isNil } from 'lodash';
import qs, { BooleanOptional, IStringifyOptions } from 'qs';
import {
  Schema,
  Dimension,
  Metric,
  Rollup,
} from './schema.model';
import { IQueryFilterWrapper, IMetricFilterWrapper } from '../../interfaces/query-filter.interface';
import { FilterDimension } from '../filter-dimension.model';
import { MetricFilter } from '../metric-filter.model';
import { Model } from '../model';
import { QueryFilter } from '../query-filter.model';
import { parseDate } from '../../utils/date.util';

const DEFAULT_LIMIT = 100;
const QUERY_STRING_OPTIONS: IStringifyOptions<BooleanOptional> = { arrayFormat: 'brackets' };

export class Query extends Model {
  dataset: string;
  schema: Schema;
  dimensions: Dimension[];
  groupBy: Dimension[];
  dimensionProperties: any;
  slices: string[];
  interval: string;
  selection: any;
  rollup: Rollup;
  metrics: Metric[];
  limit: number;
  isDirty = false;
  wasCompacted = false;
  auth: string;
  globalFilters: IQueryFilterWrapper[] = [];
  dataSlicersFilters: IQueryFilterWrapper[];
  firstDayOfWeek: string = null;

  order: string[];
  shareId: string;
  publicId: string;
  filters: IQueryFilterWrapper[] = [];
  metricFilters: IMetricFilterWrapper[] = [];
  version: number;

  constructor(obj = {}, schema: Schema = null) {
    super(obj);
    this.schema = schema;
    this.dimensions = (obj['dimensions'] || []).map(d => schema.fetchDimension(d));
    if (obj['group_by']) {
      this.groupBy = obj['group_by'].map(d => schema.fetchDimension(d));
    }
    this.dimensionProperties = this.dimensionProperties || {};

    const filteredDimensionProperties = {};
    for (const dimensionName in this.dimensionProperties) {
      const dimension = schema.fetchDimension(dimensionName);
      if (dimension) {
        filteredDimensionProperties[dimensionName] = dimension.properties
          .map(property => this.dimensionProperties[dimensionName].find(dp => dp === property.name))
          .filter(Boolean);
        if (filteredDimensionProperties[dimensionName].length !== this.dimensionProperties[dimensionName].length) {
          this.wasCompacted = true;
        }
      }
    }
    this.dimensionProperties = filteredDimensionProperties;

    if (obj['rollup'] && obj['rollup']['value']) {
      this.rollup = new Rollup(obj['rollup']['value']);
    } else {
      this.rollup = new Rollup(obj['rollup'] || 'total');
    }
    this.firstDayOfWeek = obj['first_day_of_week'];

    this.metrics = (obj['metrics'] || []).map(m => schema.fetchMetric(m));
    this.selection = this.selection || {};
    this.limit = this.limit || DEFAULT_LIMIT;
    this.auth = obj['auth'];
    this.shareId = obj['share_id'];
    this.publicId = obj['public_id'];
    this.order = obj['order'] || [];

    this.order = this.order.filter(o => {
      const [orderDimension, orderDimensionProperty] = o.replace('-', '').split('.');
      const isDimensionProperty = !!orderDimensionProperty;

      const dimensionAndPropertyMatch = isDimensionProperty && this.dimensions.find(d => d?.name === orderDimension && d?.name === orderDimensionProperty);
      const dimensionPropertyMatch = isDimensionProperty && this.dimensionProperties[orderDimension]?.find(dp => dp === orderDimensionProperty);
      const onlyDimensionMatch = !isDimensionProperty && this.dimensions.find(d => d?.name === orderDimension);
      const metricMatch = !isDimensionProperty && this.metrics.find(m => m?.name === orderDimension);

      return (dimensionAndPropertyMatch || dimensionPropertyMatch || onlyDimensionMatch || metricMatch);
    });

    const compactedFilters = this.processFilters(schema, obj['filters']);
    const compactedMetricFilters = this.processMetricFilters(schema, obj['metric_filters']);
    const compactedGlobalFilters = this.processFilters(schema, obj['global_filters'], true);
    this.filters = compactedFilters.filters;
    this.metricFilters = compactedMetricFilters.filters;
    this.version = obj['version'] || 0;

    const numDimensions = this.dimensions.length;
    const numMetrics = this.metrics.length;
    this.dimensions = this.dimensions.filter(d => !isNil(d));
    this.metrics = this.metrics.filter(m => !isNil(m));
    this.wasCompacted = numDimensions !== this.dimensions.length || numMetrics !== this.metrics.length || this.wasCompacted || compactedFilters.compact || compactedGlobalFilters.compact;
  }

  get name() {
    return `${this.dataset} query`;
  }

  clone(): Query {
    const clonedQuery = new Query(this.asJson(), this.schema);
    const globalFilters = this.formatFilters(this.globalFilters);
    clonedQuery.globalFilters = this.processFilters(this.schema, globalFilters, true).filters;

    if (this.dataSlicersFilters) {
      const dataSlicersFilters = this.formatFilters(this.dataSlicersFilters);
      clonedQuery.dataSlicersFilters = this.processFilters(this.schema, dataSlicersFilters, true).filters;
    }
    return clonedQuery;
  }

  equals(query: Query) {
    return isEqual(this.asJson(), query.asJson()) &&
      isEqual(this.globalFilters, query.globalFilters) &&
      isEqual(this.dataSlicersFilters, query.dataSlicersFilters);
  }

  isValid() {
    return !this.validate();
  }

  validate() {
    const message = [
      this.validateDimensions(),
      this.validateMetrics(),
      this.validateDates(),
    ].filter(Boolean);

    if (message.length) {
      return message;
    }
  }

  validateDimensions() {
    const dimensions = this.dimensions;

    if (dimensions.length) {
      if (!this.schema.hasCube(dimensions)) {
        return 'cube_incomplete';
      }
    }
  }

  validateMetrics() {
    const metrics = this.metrics;

    if (!metrics.length) {
      return 'no_metrics';
    }
  }

  validateDates() {
    if (!this.interval || this.interval === '') {
      return 'no_dates';
    }

    const dates = this.getDates();
    if (!dates) {
      return 'invalid_dates';
    }
  }

  getQueryUri() {
    let queryDimensions = [...(this.groupBy || this.dimensions)]
      .filter(Boolean)
      .sort((a, b) => this.dimensions.findIndex(d => d === a) - this.dimensions.findIndex(d => d === b))
      .map(dimension => dimension.name);
    if (!queryDimensions.length) {
      queryDimensions = ['_'];
    }
    const metricNames = this.metrics.map(metric => metric.name);

    let uri = [
      this.dataset,
      'series',
      queryDimensions.join(','),
      this.interval,
      queryDimensions.map(dim => '_').join(','),
      metricNames.join(','),
    ].join('/');

    if (this.rollup.value === 'week' && this.firstDayOfWeek) {
      uri += `?rollup=week:${this.firstDayOfWeek}`;
    } else {
      uri += `?rollup=${this.rollup.value}`;
    }

    if (queryDimensions[0] !== '_') {
      uri += this.getDataQuery(queryDimensions);
    }

    const selection = this.createSelection(this.filters, this.globalFilters);
    uri += `&${qs.stringify({ selection }, QUERY_STRING_OPTIONS)}`;

    if (this.dataSlicersFilters?.length > 0) {
      const datasSlicersSelection = this.createSelection(this.dataSlicersFilters);
      uri += `&${qs.stringify({ data_slicers_selection: datasSlicersSelection }, QUERY_STRING_OPTIONS)}`;
    }

    const metricSelection = this.createSelection(this.metricFilters);
    uri += `&${qs.stringify({ metric_selection: metricSelection }, QUERY_STRING_OPTIONS)}`;

    if (this.order.filter(Boolean).length) {
      uri += `&order=${this.order.join(',')}`;
    }

    if (this.limit) {
      uri += `&limit=${this.limit}`;
    }

    if (this.auth) {
      uri += `&auth=${this.auth}`;
    }

    if (this.shareId) {
      uri += `&share_id=${this.shareId}`;
    }

    if (this.publicId) {
      uri += `&public_id=${this.publicId}`;
    }
    return uri;
  }

  getSlicesUri(filterDimensions?: FilterDimension[]) {
    let queryDimensions = this.dimensions.map(dimension => dimension.name);
    let dimensionSlices: string;
    if (!queryDimensions.length) {
      queryDimensions = ['_'];
    }

    if (filterDimensions?.length) {
      dimensionSlices = queryDimensions.map(dim => {
        const dimension = filterDimensions.find(fd => fd.name === dim);
        return dimension ? dimension.value : '_';
      }).join(',');
    } else {
      dimensionSlices = queryDimensions.map(d => '_').join(',');
    }

    let uri = [
      this.dataset,
      'slices',
      queryDimensions.join(','),
      this.interval,
      dimensionSlices,
    ].join('/');

    uri += '?rollup=total';

    if (queryDimensions[0] !== '_') {
      uri += this.getDataQuery(queryDimensions);
    }

    const selection = this.createSelection(this.filters, this.globalFilters);
    uri += `&${qs.stringify({ selection }, QUERY_STRING_OPTIONS)}`;

    if (this.dataSlicersFilters?.length > 0) {
      const datasSlicersSelection = this.createSelection(this.dataSlicersFilters);
      uri += `&${qs.stringify({ data_slicers_selection: datasSlicersSelection }, QUERY_STRING_OPTIONS)}`;
    }

    if (this.limit) {
      uri += `&limit=${this.limit}`;
    }

    if (this.auth) {
      uri += `&auth=${this.auth}`;
    }
    return uri;
  }

  private getDataQuery(queryDimensions) {
    let datas = queryDimensions.map(dimension => `${dimension}.${dimension}`);
    for (const dimensionName in this.dimensionProperties) {
      for (const propertyName of this.dimensionProperties[dimensionName]) {
        datas.push(`${dimensionName}.${propertyName}`);
      }
    }
    return `&data=${datas.join(',')}`;
  }

  private createSelection(filters, globalFilters = []) {
    const combinedFilters = [...filters, ...globalFilters];
    let andBlocks = [];

    combinedFilters.forEach(filter => {
      let orBlocks = [];
      filter.expressions.forEach((expression, key) => {
        orBlocks.push(expression.queryString);
        if (filter.expressions.length - 1 === key) {
          andBlocks.push(orBlocks.join('|'));
        }
      });
    });

    return andBlocks.join(',');
  }

  getSearchUri(dimension: Dimension, term: string, limitValue: number = 20, data: string[] = []) {
    const parts = [
      this.dataset,
      'search',
      dimension.name,
      this.interval,
      dimension.name,
    ];

    if (!isNil(term)) {
      parts.push(encodeURIComponent(term));
    }

    let uri = parts.join('/');
    uri += '?limit=' + limitValue;
    if (data?.length) {
      uri += `&data=${data.join(',')}`;
    }

    return uri;
  }

  getDates() {
    if (this.interval) {
      const [startDate, endDate] = this.interval.split('-');
      if (startDate && endDate) {
        return {
          startDate: parseDate(startDate),
          endDate: parseDate(endDate),
        };
      }
    }
  }

  get hasFilters(): boolean {
    return !!this.filters.length;
  }

  get hasMetricFilters(): boolean {
    return !!this.metricFilters.length;
  }

  asJson() {
    const obj = {
      dataset: this.dataset,
      dimensions: this.dimensions.map(d => d.name),
      dimension_properties: this.dimensionProperties,
      slices: this.slices,
      interval: this.interval,
      selection: this.selection,
      rollup: this.rollup.value,
      first_day_of_week: this.firstDayOfWeek,
      metrics: this.metrics.map(m => m.name),
      order: this.order,
      limit: this.limit,
      share_id: this.shareId,
      public_id: this.publicId,
      auth: this.auth,
      filters: this.formatFilters(this.filters),
      metric_filters: this.formatMetricFilters(this.metricFilters),
      version: this.version,
    };

    if (this.groupBy) {
      obj['group_by'] = this.groupBy.filter(Boolean).map(g => g.name);
    }

    return obj;
  }

  public processFilters(schema: Schema, filters = [], isGlobal?: boolean) {
    let compact = false;
    let finalFilters = filters.map((filter: IQueryFilterWrapper) => {
      const processedFilter: IQueryFilterWrapper = {
        expressions: filter.expressions.map(expression => {
          const dimensionProperty = expression.dimensionProperty || expression['dimension_property'];

          if (dimensionProperty) {
            const dimension = schema.fetchDimension(expression.dimension?.name);
            const property = dimension?.properties?.find(p => p.name === dimensionProperty.name);
            const isStalePropertyReference = !property && expression.key?.includes('.');

            if (isStalePropertyReference) {
              const transformedName = expression.key.replace('.', '_');
              const propertyName = expression.key.split('.')[1];

              let newDimension = schema.dimensions.find(dim => dim.name === transformedName);
              let newKey = `${transformedName}.${transformedName}`;

              if (!newDimension) {
                newDimension = schema.dimensions.find(dim => dim.name === propertyName);
                newKey = `${propertyName}.${propertyName}`;
              }

              if (newDimension) {
                compact = true;
                return new QueryFilter({
                  ...expression,
                  key: newKey,
                  dimension: newDimension,
                  dimension_property: null,
                });
              }
            }
          }

          if (!expression.dimension) {
            const dimension = schema.dimensions.find(dim => dim.name === expression.key);
            return new QueryFilter({ ...expression, dimension });
          } else {
            return new QueryFilter(expression);
          }
        }).filter(expression => {
          const dimension = schema.fetchDimension(expression.dimension?.name || expression.key);

          if (!dimension) {
            compact = true;
          }
          return !!dimension;
        }),
      };

      if (isGlobal) {
        processedFilter.dataset = filter.dataset;
        processedFilter.pageId = filter.pageId;
      }

      return processedFilter;
    }).filter(filter => filter.expressions.length);
    return { filters: finalFilters, compact };
  }

  public processMetricFilters(schema: Schema, metricFilters = []) {
    let compact = false;
    let finalFilters = metricFilters.map((filter: IMetricFilterWrapper) => {
      const processedFilter: IMetricFilterWrapper = {
        expressions: filter.expressions.map(expression => {
          if (!expression.metric) {
            const metric = schema.metrics.find(dim => dim.name === expression.key);
            return new MetricFilter({ ...expression, metric });
          } else {
            return new MetricFilter(expression);
          }
        }).filter(expression => {
          const metric = schema.fetchMetric(expression.metric?.name || expression.key);
          if (!metric) {
            compact = true;
          }
          return !!metric;
        }),
      };

      return processedFilter;
    }).filter(filter => filter.expressions.length);
    return { filters: finalFilters, compact };
  }

  private formatFilters(filters) {
    return filters.map((filter: IQueryFilterWrapper) => ({
      ...filter,
      expressions: filter.expressions.map(expression => expression.asJson()),
    }));
  }

  private formatMetricFilters(filters) {
    return filters.map((filter: IMetricFilterWrapper) => ({
      ...filter,
      expressions: filter.expressions.map(expression => expression.asJson()),
    }));
  }
}
